import type { DataModelView } from '#core/lib/cognite/view.ts';

import { createEnrichedView } from '../createEnrichedView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import { STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import { type EnrichGroup, type Shape, STRING_KEEP } from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';

/**
 * The per-family spec fields {@link makeStringContextViews} reads off each row:
 * the agent-facing name + doc and the de-prefixed measure groups. The producing
 * `source` is supplied by the factory config's per-spec selector, never read
 * off the spec here, so producers whose source varies per row carry it on
 * their own spec alias.
 */
type StringContextSpec = {
  readonly name: string;
  readonly description: string;
  readonly groups?: readonly EnrichGroup[];
  /** Per-spec keep override — for sources whose identity/relation columns deviate from the family default. */
  readonly keep?: Shape;
};

/**
 * The producing CDF view a StringContext family fronts, resolved per spec
 * row. Families with one fixed view pass `() => View`.
 */
type StringContextSource<SpecType extends StringContextSpec> = (spec: SpecType) => DataModelView;

/**
 * Build the per-string StringContext hook for one catalog family: the shared
 * `useCatalogEnrichContext()` prelude, then `createEnrichedView` over each spec
 * with the StringContext well/field/reservoir ancestry and the client +
 * timeseries minter pre-bound. The nine scalar-grain per-string producers differ
 * only in their `source` selector, their `keep` shape
 * (identity + the producing-string relation), and their spec table — so each
 * collapses to one `makeStringContextViews({ source, keep?, specs })` call.
 *
 * The two timestamp-aligned per-string families stay on `createAlignedSeriesView`
 * directly: their spec carries a single required `EnrichGroup` (not the optional
 * `EnrichGroup[]` here) and a distinct enrichment surface, so folding them in
 * would force an unsound union.
 */
export const makeStringContextViews = <SpecType extends StringContextSpec>({
  source,
  keep = STRING_KEEP,
  specs,
}: {
  readonly source: StringContextSource<SpecType>;
  readonly keep?: Shape;
  readonly specs: readonly SpecType[];
}): (() => readonly VirtualView[]) =>
  // Named `use*` so the hook-call rules recognise the returned function as a
  // custom hook even though its public identity is the producer's own
  // `useXxxViews` binding at the call site.
  function useStringContextFamily(): readonly VirtualView[] {
    const { client, mintSeries } = useCatalogEnrichContext();
    return specs.map((spec) =>
      createEnrichedView({
        name: spec.name,
        description: spec.description,
        source: source(spec),
        keep: spec.keep ?? keep,
        groups: spec.groups,
        enrich: STRING_CONTEXT_ENRICH,
        client,
        mintSeries,
      }),
    );
  };
